import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';

import FaceIcon from '@mui/icons-material/Face';
import EmailIcon from '@mui/icons-material/Email';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LoyaltyIcon from '@mui/icons-material/Loyalty';

import { useEffect } from 'react';
import { API } from 'aws-amplify';

export default function Profile(props) {
  const [info, setInfo] = React.useState(null); // null = not loaded yet
  const [loadingInfo, setLoadingInfo] = React.useState(false);
  const [cancelling, setCancelling] = React.useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchInfo = async () => {
      try {
        // only regular users have payments info
        if (props?.userData?.type !== 'regular') {
          if (!cancelled) setInfo(undefined);
          return;
        }

        setLoadingInfo(true);
        const path = `payments?type=info`;
        const response = await API.post('payments', path);

        if (!cancelled) {
          // response could be {} or have stripe but no subscription — still store it
          setInfo(response ?? undefined);
        }
      } catch (e) {
        console.log('getInfo error:', e);
        if (!cancelled) setInfo(undefined);
        alert(e?.message ?? 'Failed to load billing info.');
      } finally {
        if (!cancelled) setLoadingInfo(false);
      }
    };

    fetchInfo();

    return () => {
      cancelled = true;
    };
    // refetch if the signed-in user changes, or parent triggers a refresh by changing updateUser reference/state
  }, [props?.userData?.type, props?.userData?.id]);

  const timeConverter = (UNIX_timestamp) => {
    if (!UNIX_timestamp) return '';
    const a = new Date(UNIX_timestamp * 1000);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${a.getDate()} ${months[a.getMonth()]} ${a.getFullYear()}`;
  };

  const cancelSubscription = async () => {
    const renewal = info?.subscription?.renewal_date ? timeConverter(info.subscription.renewal_date) : 'your renewal date';

    const ok = window.confirm(
      `Are you sure you want to cancel? You will lose the days between now and ${renewal} as this will take effect immediately.`
    );
    if (!ok) return;

    try {
      setCancelling(true);
      const path = `payments?type=subscription&update=cancel`;
      const response = await API.post('payments', path);

      if (response) {
        alert('Successfully completed. To resubscribe select a camera to record.');

        // refresh local info after cancel so UI updates immediately
        // (and also let parent refresh user data if you need it elsewhere)
        setInfo((prev) =>
          prev
            ? { ...prev, stripe: { ...(prev.stripe ?? {}), subscription: undefined }, subscription: undefined }
            : prev
        );

        props?.updateUser?.();
      } else {
        alert('Cancellation failed (empty response).');
      }
    } catch (e) {
      console.log('cancelSubscription error:', e);
      alert(e?.message ?? 'Failed to cancel subscription.');
    } finally {
      setCancelling(false);
    }
  };

  // Helpers for rendering rules
  const hasStripeCustomer = Boolean(info?.stripe?.customerId || info?.customerId || info?.stripe?.customer);
  const hasCard = Boolean(info?.card?.last4);

  const hasSubscription = Boolean(info?.subscription);

  // normalize subscription location (your code uses info.subscription; your desired rule says info.stripe.subscription)
  const subscription = info?.subscription ?? null;

  return (
    <Paper sx={{ maxWidth: 936, margin: 'auto', overflow: 'hidden' }}>
      <AppBar position="static" color="default" elevation={3} sx={{ borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
        <Toolbar>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={4}>
              <FaceIcon />
            </Grid>
            <Grid item xs={6}>
              <Typography>
                {props?.userData?.firstname}, {props?.userData?.lastname}
              </Typography>
            </Grid>
          </Grid>
        </Toolbar>

        <Toolbar>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={4}>
              <EmailIcon />
            </Grid>
            <Grid item xs={8}>
              <Typography>{props?.userAttributes?.email}</Typography>
            </Grid>
          </Grid>
        </Toolbar>

        {/* Billing section (show if we loaded info and they are regular) */}
        {loadingInfo ? (
          <Toolbar>
            <Typography>Loading billing info…</Typography>
          </Toolbar>
        ) : info ? (
          <div>
            {/* Show card if present */}
            {(hasStripeCustomer || hasCard) && (
              <Toolbar>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={4}>
                    <CreditCardIcon />
                  </Grid>

                  <Grid item xs={8}>
                    {info?.card?.last4 ? (
                      <Typography>
                        ****{info.card.last4} {info.card.brand ?? ''}
                      </Typography>
                    ) : (
                      <Typography>No card on file yet</Typography>
                    )}
                  </Grid>
                </Grid>
              </Toolbar>
            )}

            {/* Subscription section ONLY if subscription exists */}
            {hasSubscription && subscription ? (
              <Toolbar>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={4}>
                    <LoyaltyIcon />
                  </Grid>

                  <Grid item xs={8}>
                    <Typography>
                      subscription renews on{' '}
                      {subscription?.renewal_date ? timeConverter(subscription.renewal_date) : '(renewal date unavailable)'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      disabled={cancelling}
                      onClick={cancelSubscription}
                    >
                      {cancelling ? 'Cancelling…' : 'Cancel Subscription and remove credit card on file'}
                    </Button>
                  </Grid>
                </Grid>
              </Toolbar>
            ) : (
              <Toolbar>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={4}>
                    <LoyaltyIcon />
                  </Grid>
                  <Grid item xs={8}>
                    <Typography>No active subscription</Typography>
                  </Grid>
                </Grid>
              </Toolbar>
            )}
          </div>
        ) : null}
      </AppBar>
    </Paper>
  );
}
